import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import {
  useModalStack,
  ModalStackProvider,
  isInsideRadixPortal,
  Z_INDEX_MODAL_CONTENT,
  Z_INDEX_STEP,
} from "@/lib/modal-stack";

// Context for sharing scroll state between dialog parts
interface DialogScrollContextValue {
  canScrollUp: boolean;
  canScrollDown: boolean;
  setScrollInfo: (info: {
    canScrollUp: boolean;
    canScrollDown: boolean;
  }) => void;
}

const DialogScrollContext = React.createContext<DialogScrollContextValue>({
  canScrollUp: false,
  canScrollDown: false,
  setScrollInfo: () => {},
});

const FALLBACK_DIALOG_TITLE = "Dialog";

function hasDialogTitle(children: React.ReactNode): boolean {
  let foundTitle = false;

  React.Children.forEach(children, (child) => {
    if (foundTitle || !React.isValidElement(child)) {
      return;
    }

    if (
      child.type === DialogTitle ||
      child.type === DialogPrimitive.Title ||
      child.props["data-slot"] === "dialog-title"
    ) {
      foundTitle = true;
      return;
    }

    if ("children" in child.props) {
      foundTitle = hasDialogTitle(child.props.children);
    }
  });

  return foundTitle;
}

function Dialog({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />;
}

function DialogTrigger({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

function DialogOverlay({
  className,
  style,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      data-slot="dialog-overlay"
      className={cn(
        "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:fill-mode-forwards fixed inset-0 bg-black/50",
        className
      )}
      style={style}
      {...props}
    />
  );
}

function DialogContent({
  className,
  children,
  contentWrapperClassName,
  contentWrapperMode = "default",
  overlayClassName,
  positionMode = "center",
  showCloseButton = true,
  onInteractOutside: onInteractOutsideProp,
  style,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Content> & {
  contentWrapperClassName?: string;
  contentWrapperMode?: "default" | "custom";
  overlayClassName?: string;
  positionMode?: "center" | "custom";
  showCloseButton?: boolean;
}) {
  const depth = useModalStack();
  const zIndex = Z_INDEX_MODAL_CONTENT + (depth > 0 ? depth * Z_INDEX_STEP : 0);
  const needsFallbackTitle = !hasDialogTitle(children);

  const [scrollInfo, setScrollInfo] = React.useState({
    canScrollUp: false,
    canScrollDown: false,
  });

  // Prevent closing when clicking on portaled Radix content (popovers, dropdowns, selects).
  // For portal clicks, e.preventDefault() suppresses the dismiss and the prop is not
  // called. For legitimate outside clicks (including overlay clicks) the prop is called as normal.
  const handleInteractOutside = React.useCallback(
    (e: Parameters<NonNullable<typeof onInteractOutsideProp>>[0]) => {
      const target = e.target;
      // Allow closing when clicking on the dialog overlay itself
      if (
        target instanceof Element &&
        target.closest('[data-slot="dialog-overlay"]')
      ) {
        onInteractOutsideProp?.(e);
        return;
      }
      if (isInsideRadixPortal(e)) {
        e.preventDefault();
        return;
      }
      onInteractOutsideProp?.(e);
    },
    [onInteractOutsideProp]
  );

  return (
    <DialogScrollContext.Provider value={{ ...scrollInfo, setScrollInfo }}>
      <DialogPortal data-slot="dialog-portal">
        <DialogOverlay className={overlayClassName} style={{ zIndex }} />
        <DialogPrimitive.Content
          data-slot="dialog-content"
          className={cn(
            "bg-white flex flex-col",
            positionMode === "center" &&
              "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:fill-mode-forwards fixed top-[50%] left-[50%] w-full max-w-[calc(100%-2rem)] max-h-[calc(100vh-2rem)] overflow-hidden translate-x-[-50%] translate-y-[-50%] rounded-lg shadow-lg duration-200",
            className
          )}
          style={{ ...style, zIndex }}
          {...props}
          onInteractOutside={handleInteractOutside}
        >
          <div
            className={cn(
              "relative flex flex-col",
              contentWrapperMode === "default" && "overflow-hidden px-3",
              contentWrapperClassName
            )}
          >
            <ModalStackProvider parentDepth={depth}>
              {needsFallbackTitle ? (
                <DialogTitle className="sr-only">
                  {FALLBACK_DIALOG_TITLE}
                </DialogTitle>
              ) : null}
              {children}
            </ModalStackProvider>
            {showCloseButton && (
              <DialogPrimitive.Close
                data-slot="dialog-close"
                className="ring-offset-white focus:ring-blue-500 absolute top-0 right-2 p-0 leading-none text-neutral-400 transition-colors hover:text-neutral-900 focus:ring-2 focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none cursor-pointer"
              >
                <i className="fa-regular fa-xmark text-lg"></i>
                <span className="sr-only">Close</span>
              </DialogPrimitive.Close>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogScrollContext.Provider>
  );
}

// Scrollable body component that tracks scroll state
function DialogBody({
  className,
  children,
  ...props
}: React.ComponentProps<"div">) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const { setScrollInfo, canScrollUp, canScrollDown } =
    React.useContext(DialogScrollContext);

  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollUp = el.scrollTop > 0;
    const scrollDown = el.scrollTop + el.clientHeight < el.scrollHeight - 1;

    setScrollInfo({ canScrollUp: scrollUp, canScrollDown: scrollDown });
  }, [setScrollInfo]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    // Initial check
    updateScrollState();

    // Check on scroll
    el.addEventListener("scroll", updateScrollState, { passive: true });

    // Check on resize (content might change)
    const resizeObserver = new ResizeObserver(updateScrollState);
    resizeObserver.observe(el);

    return () => {
      el.removeEventListener("scroll", updateScrollState);
      resizeObserver.disconnect();
    };
  }, [updateScrollState]);

  return (
    <div className="relative flex-1 flex flex-col min-h-0">
      <div
        ref={scrollRef}
        data-slot="dialog-body"
        className={cn("flex-1 overflow-y-auto pb-2", className)}
        {...props}
      >
        {children}
      </div>
      {/* Top shadow - shows when scrolled down */}
      <div
        className={cn(
          "absolute top-0 left-0 right-0 h-8 pointer-events-none transition-opacity duration-150 bg-gradient-to-b from-black/15 to-transparent z-10",
          canScrollUp ? "opacity-100" : "opacity-0"
        )}
      />
      {/* Bottom shadow - shows when more content below */}
      <div
        className={cn(
          "absolute bottom-0 left-0 right-0 h-8 pointer-events-none transition-opacity duration-150 bg-gradient-to-t from-black/15 to-transparent z-10",
          canScrollDown ? "opacity-100" : "opacity-0"
        )}
      />
    </div>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-header"
      className={cn(
        "flex flex-col gap-2 text-center sm:text-left pt-3",
        className
      )}
      {...props}
    />
  );
}

function DialogFooter({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        "flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pt-4 pb-3",
        className
      )}
      {...props}
    />
  );
}

function DialogTitle({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn(
        "font-gotham text-2xl font-bold text-neutral-900 leading-none",
        className
      )}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn("text-neutral-500 text-sm", className)}
      {...props}
    />
  );
}

export {
  Dialog,
  DialogBody,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
